import { $color } from "@/utils/cssVarUtils"
import { tabsAnatomy as parts } from "@chakra-ui/anatomy"
import type {
  PartsStyleFunction,
  PartsStyleInterpolation,
  PartsStyleObject,
  SystemStyleObject,
} from "@chakra-ui/theme-tools"
import { sizesOptions } from "../common/sizeOptions"

const baseStyleTab: SystemStyleObject = {
  _focus: {
    boxShadow: "none",
  },
}

const baseStyleTablist: SystemStyleObject = {
  fontWeight: 600,
  px: 0,
}

const baseStyle: PartsStyleObject<typeof parts> = {
  tab: baseStyleTab,
  tablist: baseStyleTablist,
}

const baseSizes: Record<string, PartsStyleObject<typeof parts>> = {
  xs: {
    tablist: {
      ...sizesOptions.xs,
      px: 0,
    },
    tab: {
      minW: 'md',
      px: sizesOptions.xs.px,
    },
    tabpanel: {
      p: '3xs',
    }
  },
  sm: {
    tablist: {
      ...sizesOptions.sm,
      px: 0,
    },
    tab: {
      minW: '2xl',
      px: sizesOptions.sm.px,
    },
    tabpanel: {
      p: '3xs',
    }
  },
  md: {
    tablist: {
      ...sizesOptions.md,
      px: 0,
    },
    tab: {
      minW: '5xl',
      px: sizesOptions.md.px,
    },
    tabpanel: {
      p: '2xs'
    }
  },
  lg: {
    tablist: {
      ...sizesOptions.lg,
      px: 0,
    },
    tab: {
      minW: '10xl',
      px: sizesOptions.lg.px,
    },
    tabpanel: {
      p: '2xs'
    }
  },
}

const commonStyles = {
  fontSize: 'inherit',
  fontWeight: "inherit",
  borderRadius: 'inherit',
  _active: {
    bg: '',
  },
  _disabled: {
    opacity: 0.3,
    cursor: "not-allowed",
  },
}

const variantLine: PartsStyleFunction<typeof parts> = (props) => {
  const { isFitted, colorScheme: c  } = props

  return {
    tablist: {
      display: isFitted ? 'flex' : 'inline-flex',
      borderBottomWidth: '1px',
      borderRadius: 'none',
    },
    tab: {
      color: $color('text-masked'),
      _selected: {
        color: $color(`scene-${c}-normal`),
        borderColor: "currentColor",
      },
      ...commonStyles,
    },
  }
}

const variantRounded: PartsStyleFunction<typeof parts> = (props) => {
  const { size, isFitted, colorScheme: c } = props

  let selectedColor = $color('dark-text-normal')
  if (['success', 'warning'].includes(c)) {
    selectedColor = $color('light-text-normal')
  }

  return {
    tablist: {
      bg: $color('bg-input-normal'),
      display: isFitted ? 'flex' : 'inline-flex',
    },
    tab: {
      color: $color('text-normal'),
      _selected: {
        color: selectedColor,
        bg: $color(`scene-${c}-normal`),
      },
      ...commonStyles,
    },
  }
}


const variants: Record<string, PartsStyleInterpolation<typeof parts>> = {
  "line": variantLine,
  "rounded": variantRounded,
}


const defaultProps = {
  size: "xs",
  variant: "rounded",
  colorScheme: 'primary',
}


export const Tabs = {
  parts: parts.keys,
  baseStyle,
  sizes: baseSizes,
  defaultProps,
  variants,
}